/**
 * Auth context — owns the Firebase user + Firestore profile in one place.
 *
 * Profile lifecycle:
 *   1. First Google sign-in → no `users/{uid}` doc exists → we create it
 *      with `status: 'pending'` and `pendingRole` set from the signup
 *      role pick. User lands on IFAVerify (IFA) or ApprovalPending
 *      (Individual).
 *   2. IFA submits SEBI registration → write `sebiRegistration` +
 *      `acceptedActiveSebi` + `acceptedResearchAid` onto the user doc.
 *      Status stays `pending` until admin approves.
 *   3. Admin approves via Firebase Console (manual gmail-approval flow
 *      per scope decision) → status flips to `active` → user signs in
 *      and lands on their workspace home.
 *   4. `subscribed` is independent — both pending and active users may
 *      have `subscribed: false`. Briefing-generation CTAs check this.
 *
 * The auth context exposes:
 *   user             → Firebase User object (null when signed out)
 *   profile          → UserProfile from Firestore (null on first sign-in
 *                       before the doc is created, or when signed out)
 *   loading          → true while either auth or profile is initializing
 *   signInWithGoogle → kicks off the popup flow; creates the profile
 *                       doc with the supplied role if it's a first sign-in
 *   signOut          → Firebase signOut
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import {
  signInWithPopup,
  signOut as fbSignOut,
  onAuthStateChanged,
  type User as FirebaseUser,
} from "firebase/auth";
import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  type DocumentSnapshot,
} from "firebase/firestore";
import { auth, db, googleProvider } from "@/firebase";
import { switchAccountRole } from "@/lib/api";
import { track } from "@/lib/track";
import type { UserProfile, UserRole } from "@/types";

interface AuthContextValue {
  user: FirebaseUser | null;
  profile: UserProfile | null;
  loading: boolean;
  /** True when we gave up waiting for the profile. Lets a screen show an
   *  actionable "couldn't load your profile — retry" instead of an
   *  indefinite spinner. */
  profileUnavailable: boolean;
  signInWithGoogle: (role: UserRole) => Promise<UserProfile>;
  signOut: () => Promise<void>;
}

/** How long the app may sit on the splash waiting for the profile document
 *  before falling back to a one-shot read. Chosen to be well under the point
 *  where a user decides the app is broken (observed hang: 60s+). */
const PROFILE_GATE_MS = 6000;

const AuthContext = createContext<AuthContextValue | null>(null);

/** Hydrate a Firestore snapshot into a UserProfile, filling in
 *  reasonable defaults so type narrowing downstream stays clean. */
function hydrateProfile(snap: DocumentSnapshot, fbUser: FirebaseUser): UserProfile | null {
  if (!snap.exists()) return null;
  const data = snap.data();
  return {
    uid: fbUser.uid,
    email: fbUser.email ?? data.email ?? "",
    displayName: fbUser.displayName ?? data.displayName ?? "",
    photoURL: fbUser.photoURL ?? undefined,
    role: (data.role ?? "individual") as UserRole,
    status: data.status ?? "pending",
    createdAt: data.createdAt?.toMillis?.() ?? Date.now(),
    activatedAt: data.activatedAt?.toMillis?.(),
    sebiRegistration: data.sebiRegistration,
    acceptedActiveSebi: data.acceptedActiveSebi,
    acceptedResearchAid: data.acceptedResearchAid,
    subscribed: data.subscribed ?? false,
    subscriptionProvider: data.subscription_provider,
    subscriptionExpiresAt: data.subscription_expires_at?.toMillis?.(),
    subscriptionAutoRenew: data.subscription_auto_renew,
  };
}

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileUnavailable, setProfileUnavailable] = useState(false);

  // Track Firebase auth state. When user signs in, subscribe to their
  // profile doc; when they sign out, drop the profile listener.
  useEffect(() => {
    let profileUnsub: (() => void) | null = null;

    const authUnsub = onAuthStateChanged(auth, (fbUser) => {
      // Clean up the previous profile listener whenever auth flips.
      profileUnsub?.();
      profileUnsub = null;

      setUser(fbUser);
      if (!fbUser) {
        setProfile(null);
        setLoading(false);
        return;
      }

      // Live-subscribe to the user's Firestore doc so the
      // ApprovalPending screen auto-transitions when admin flips
      // status to "active". No polling needed.
      const ref = doc(db, "pro_users", fbUser.uid);
      let settled = false;
      // `let`, declared BEFORE settle(): onSnapshot can deliver a cached doc
      // synchronously, and a const declared below would be in its temporal dead
      // zone at that moment — the gate fix would itself throw.
      let timer: ReturnType<typeof setTimeout> | undefined;
      const settle = () => { settled = true; if (timer) clearTimeout(timer); setLoading(false); };

      // A Listen channel that HANGS calls neither callback, so the error handler
      // below is not enough: the app was observed sitting on the splash for 60s+
      // with two Firestore Listen/channel requests pending and nothing else in
      // flight. Time-box the gate and fall back to a one-shot read, which uses a
      // plain request rather than the long-lived channel.
      timer = setTimeout(() => {
        if (settled) return;
        console.warn("[auth] profile snapshot did not resolve in time — one-shot read");
        getDoc(ref)
          .then((snap) => { if (!settled) { setProfile(hydrateProfile(snap, fbUser)); settle(); } })
          .catch((err) => {
            console.warn("[auth] profile fallback read failed:", err);
            if (!settled) { setProfileUnavailable(true); settle(); }
          });
      }, PROFILE_GATE_MS);

      profileUnsub = onSnapshot(
        ref,
        (snap) => {
          // Still hydrate if this arrives after the fallback — it's the truth.
          setProfile(hydrateProfile(snap, fbUser));
          setProfileUnavailable(false);
          settle();
        },
        (err) => {
          // Network blip / permission issue — keep the existing
          // profile in state, mark loading false so the UI doesn't
          // hang forever.
          console.warn("[auth] profile snapshot error:", err);
          setProfileUnavailable(true);
          settle();
        },
      );
    });

    return () => {
      authUnsub();
      profileUnsub?.();
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      profile,
      loading,
      profileUnavailable,

      async signInWithGoogle(role) {
        // Someone who arrived here from SignIn ("no profile yet → pick a
        // role") or from the registration form's "Continue as Individual"
        // link is ALREADY signed in. Reuse that session instead of opening a
        // second Google popup: on phones the second popup was blocked or
        // dismissed often enough that the role pick never landed.
        const fbUser = auth.currentUser ?? (await signInWithPopup(auth, googleProvider)).user;
        const ref = doc(db, "pro_users", fbUser.uid);
        const existing = await getDoc(ref);

        if (existing.exists()) {
          const current = hydrateProfile(existing, fbUser)!;
          if (current.role === role) return current;
          // Returning user who picked a DIFFERENT card. This used to return the
          // stored role untouched, so an account that had once chosen an
          // adviser card was sent to the SEBI form on every sign-in, and that
          // form's "Continue as Individual Investor" link led straight back to
          // it. The switch is server-owned because it must set `status` too
          // (rules lock it): individual → active now, adviser → pending.
          const res = await switchAccountRole(role);
          return {
            ...current,
            role: res.role,
            status: res.status,
            sebiRegistration: undefined,
            acceptedActiveSebi: undefined,
            acceptedResearchAid: undefined,
          };
        }

        // First sign-in — create the profile doc with the role they
        // picked on the signup screen.
        //
        // RETAIL IS ACTIVE IMMEDIATELY (2026-08-30). `pending` meant "waiting
        // for an admin to approve you", which stopped being true for retail
        // when the paywall came down — there is nothing left to approve. The UI
        // already routes around it, but leaving them pending made every
        // `status === "active"` check treat a legitimate user as unapproved,
        // and made the funnel count them as never activated. Advisers still
        // start pending: their SEBI registration genuinely is reviewed.
        const newProfile: Omit<UserProfile, "createdAt"> & { createdAt: ReturnType<typeof serverTimestamp> } = {
          uid: fbUser.uid,
          email: fbUser.email ?? "",
          displayName: fbUser.displayName ?? "",
          photoURL: fbUser.photoURL ?? undefined,
          role,
          status: role === "individual" ? "active" : "pending",
          subscribed: false,
          createdAt: serverTimestamp(),
        };
        await setDoc(ref, newProfile);
        // The funnel step that turns an anonymous visitor into an account. Paired
        // with `signup_click`, the gap between the two is the drop-off inside the
        // Google popup — otherwise invisible.
        track("role_selected", { role, variant: "created" });

        return {
          ...newProfile,
          createdAt: Date.now(),
        } as UserProfile;
      },

      async signOut() {
        await fbSignOut(auth);
      },
    }),
    [user, profile, loading, profileUnavailable],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Hook for any screen that needs auth state. Throws if used outside
 *  AuthProvider (catches forgotten provider wiring early). */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
